import { useGateValue } from '@statsig/react-bindings'
import { PermissionEnum } from 'api/userPermissions'
import { AuthGuard, useAuthContext } from 'components/auth'
import { getShiftTimezone } from 'lib/shift'
import { FeatureGates } from 'lib/statsig/feature_gates'
import { useLeadShifts } from 'queries/shift'
import { Box, Button, Carousel, Flex, Heading, Pluralize } from '@workwhile/ui'
import { useShiftDetail } from '../useShiftDetail'
import { LeadShiftCard } from './LeadShiftCard'
import { LinkedShiftCard } from './LinkedShiftCard'
import { MissingShiftLeads } from './MissingShiftLeads'

export const ShiftDetailLeads = () => {
  const { shift } = useShiftDetail()
  const { company } = useAuthContext()
  const isW2LeadPositionsEnabled = useGateValue(
    FeatureGates.EnableW2LeadPositions
  )
  // Skip the request until the flag is on. With the flag off, the carousel
  // keeps using the lead shifts that come with the shift detail response.
  const { data: leadShiftsInfo } = useLeadShifts(
    { companyId: company?.id, shiftId: shift?.id },
    { enabled: isW2LeadPositionsEnabled }
  )

  if (!shift) return null

  const leadShifts = isW2LeadPositionsEnabled
    ? leadShiftsInfo?.leadShifts
    : (shift.leadShifts ?? []).map((leadShift) => ({
        id: Number(leadShift.id),
        workersNeeded: leadShift.workersNeeded ?? 0,
      }))

  if (!leadShifts) return null

  if (leadShifts.length === 0) {
    return leadShiftsInfo && company ? (
      <MissingShiftLeads
        companyId={company.id}
        shiftId={shift.id}
        timezone={getShiftTimezone(shift)}
        workersNeeded={shift.workersNeeded ?? 0}
        canCreateLeadShifts={leadShiftsInfo.canCreateLeadShifts}
        leadCompanyId={leadShiftsInfo.leadCompanyId}
        defaultLeadPositionId={leadShiftsInfo.defaultLeadPositionId}
        hasLeadPosition={leadShiftsInfo.hasLeadPosition}
        hasBillRate={leadShiftsInfo.hasBillRate}
        paymentType={leadShiftsInfo.paymentType}
      />
    ) : null
  }

  const numLeadsRequested = leadShifts.reduce((acc, leadShift) => {
    return acc + (leadShift.workersNeeded || 0)
  }, 0)

  return (
    <Box mb={2}>
      <Flex alignItems="center" justifyContent="space-between">
        <Heading level={2} fontWeight={2}>
          Shift{' '}
          <Pluralize
            hideCount
            count={numLeadsRequested}
            oneText="Lead"
            manyText="Leads"
          />
        </Heading>
        {leadShiftsInfo ? (
          <AuthGuard.Permission value={PermissionEnum.ManageShifts}>
            <Button kind="medium" variant="secondary">
              Edit linked shifts
            </Button>
          </AuthGuard.Permission>
        ) : null}
      </Flex>
      {leadShiftsInfo ? (
        <Carousel mx={-2} py={3} cardsToShow={[1, 2, 2, 2, 3]} gutter={2}>
          {leadShiftsInfo.leadShifts.map((leadShift) => (
            <LinkedShiftCard key={leadShift.id} shift={leadShift} />
          ))}
        </Carousel>
      ) : (
        <Carousel mx={-2} py={3} cardsToShow={[1, 2, 2, 3, 4]} gutter={2}>
          {leadShifts.map((leadShift) => (
            <LeadShiftCard key={leadShift.id} leadShift={leadShift} />
          ))}
        </Carousel>
      )}
    </Box>
  )
}
